import { createMultiRuleValidation, type ValidationRule } from 'v2/shared/validation-schemas'
import z from 'zod'

/**
 * Checks if password contains the user's name (case-insensitive)
 * Skips check if name is shorter than 3 characters to avoid false positives
 */
export const containsName = (password: string, name: string): boolean => {
  const trimmedName = name?.trim().toLowerCase()
  const trimmedPassword = password?.trim().toLowerCase()

  // Skip check if name is too short (< 3 chars) or empty
  if (!trimmedName || trimmedName.length < 3) return false
  if (!trimmedPassword) return false

  return trimmedPassword.includes(trimmedName)
}

const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'yahoo.com',
  'yahoo.in',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'icloud.com',
  'me.com',
  'protonmail.com',
])

export const validateEmail = () => {
  return z.string().superRefine((val, ctx) => {
    if (!val) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email is required',
      })
      return
    }
    if (!z.string().email().safeParse(val).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid email',
      })
      return
    }
    if (val.length > 75) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email cannot exceed 75 characters',
      })
      return
    }
    const domain = val.split('@')[1]?.toLowerCase()
    if (domain && BLOCKED_EMAIL_DOMAINS.has(domain)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please use a work email address',
      })
    }
  })
}

/**
 * Standard password validation rules
 */
export const passwordValidationRules: ValidationRule[] = [
  { test: (v) => v.length >= 12, message: 'Must be at least 12 characters' },
  { test: (v) => /[0-9]/.test(v), message: 'Must contain at least 1 digit' },
  { test: (v) => /[A-Z]/.test(v), message: 'Must contain at least 1 uppercase character' },
  { test: (v) => /[a-z]/.test(v), message: 'Must contain at least 1 lowercase character' },
  {
    test: (v) => /[!@#$%^&*(),.?":{}|<>]/.test(v),
    message: 'Must contain at least 1 special character',
  },
]

/**
 * Creates password validation rules with name/email checks
 */
export const createPasswordRulesWithContext = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
): ValidationRule[] => {
  const rules: ValidationRule[] = [...passwordValidationRules]

  if (firstName) {
    rules.push({
      test: (v) => !containsName(v, firstName),
      message: 'Password cannot contain your first name',
    })
  }
  if (lastName) {
    rules.push({
      test: (v) => !containsName(v, lastName),
      message: 'Password cannot contain your last name',
    })
  }
  if (email) {
    rules.push({
      test: (v) => !containsName(v, email.split('@')[0]),
      message: 'Password cannot contain your email username',
    })
  }

  return rules
}

/**
 * Password validation using the multi-rule validation helper.
 * Use with `errorClassName="error-list-format"` in GenericForm for bulleted errors.
 */
export const validatePassword = (
  firstName: string | null = null,
  lastName: string | null = null,
  email: string | null = null,
) => {
  const rules = createPasswordRulesWithContext(firstName, lastName, email)
  return createMultiRuleValidation(rules)
}
